/**
 * Custom hooks for Firestore data
 */

import { useState, useEffect } from 'react';
import { onSnapshot, query, where } from 'firebase/firestore';
import {
  getLeaderboardCollection,
  getCompetitionsCollection,
  getUserProfileDoc,
  getUserActivitiesCollection,
  getChatCollection
} from '../services/firestore';

/**
 * Hook for leaderboard data
 */
export const useLeaderboard = () => {
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    const unsubscribe = onSnapshot(getLeaderboardCollection(), (snapshot) => {
      setLeaderboard(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => unsubscribe();
  }, []);

  return leaderboard;
};

/**
 * Hook for competitions data
 * Henter kun konkurranser brukeren er deltaker i
 */
export const useCompetitions = (userId) => {
  const [competitions, setCompetitions] = useState([]);

  useEffect(() => {
    if (!userId) {
      setCompetitions([]);
      return;
    }

    // Hent kun konkurranser hvor brukeren er deltaker
    const competitionsQuery = query(
      getCompetitionsCollection(),
      where('participants', 'array-contains', userId)
    );

    const unsubscribe = onSnapshot(competitionsQuery, (snapshot) => {
      setCompetitions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => unsubscribe();
  }, [userId]);

  return competitions;
};

/**
 * Hook for user profile
 */
export const useUserProfile = (userId, defaultName = '') => {
  const [profile, setProfile] = useState({ avatar: "🏃‍♂️", name: "" });

  useEffect(() => {
    if (!userId) return;

    const unsubscribe = onSnapshot(getUserProfileDoc(userId), (snapshot) => {
      if (snapshot.exists()) {
        setProfile(snapshot.data());
      } else if (defaultName) {
        setProfile({ name: String(defaultName), avatar: "🏃‍♂️" });
      }
    });

    return () => unsubscribe();
  }, [userId, defaultName]);

  return [profile, setProfile];
};

/**
 * Hook for user activities
 */
export const useUserActivities = (userId) => {
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    if (!userId) return;

    const unsubscribe = onSnapshot(getUserActivitiesCollection(userId), (snapshot) => {
      setActivities(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => unsubscribe();
  }, [userId]);

  return activities;
};

/**
 * Hook for competition chat
 */
export const useCompetitionChat = (competitionId) => {
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    if (!competitionId) return;

    const unsubscribe = onSnapshot(getChatCollection(competitionId), (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMessages(msgs.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)));
    });

    return () => unsubscribe();
  }, [competitionId]);

  return messages;
};
